import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchPlansPage } from "../services/plansService";

export type PlansCreatedStatus = "loading" | "ready" | "failed";

export interface PlansCreatedState {
  /** Whether the workspace sells anything yet. `false` until the answer is in. */
  plansCreated: boolean;
  status: PlansCreatedStatus;
}

export const PLANS_CREATED_QUERY_KEY = ["brand", "plans-created"] as const;

/**
 * Whether the workspace has created a plan — one bit, asked by every screen
 * that has to know whether there is anything on the books yet.
 *
 * Two of them so far: the second tick on the dashboard's setup checklist, and
 * the gate over Add client and New enquiry — see `useSetupGate`. It lives in
 * the brand feature because that is where a plan is made and read; the
 * screens that ask are elsewhere.
 *
 * The first page rather than `fetchPlans`, which the Plans tab uses:
 * that walks every page to build a list, and this asks one bit — whether there
 * is a single plan on the books. A workspace with a hundred of them answers
 * that on the first response, so the walk would be pages fetched to be counted
 * and thrown away.
 *
 * Backed by TanStack Query, under its own key rather than `usePlans`'s
 * `["brand", "plans"]`: this reads one page of a differently-shaped
 * response, and the two must not overwrite each other's cache entry.
 *
 * Quiet on failure, the same as `useBrandSetUp` and for the same reason: nobody
 * came to these screens to look at their plans, so a toast about them would
 * be noise on top of whatever the screen's own load is already saying. Each
 * caller decides what to do with a `failed` — the dashboard drops the
 * checklist, and the gate stays open, since not knowing is not the same as
 * knowing there is nothing there.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function usePlansCreated(): PlansCreatedState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: PLANS_CREATED_QUERY_KEY,
    queryFn: ({ signal }) => fetchPlansPage(getToken, 1, signal),
  });

  return {
    plansCreated: (query.data?.plans.length ?? 0) > 0,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}

export default usePlansCreated;
